import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import axios from 'axios';
import './TrackingModal.css'; // We will update this file next



// --- Define the default empty state for the form ---
const initialFormData = {
  initialScreeningComment: '',
  onlineAssessmentScore: '',
  onlineAssessmentStatus: 'Pending', // Use 'Pending' as a default
  round1Date: '',
  round1Comment: '',
  round1Status: 'Pending',
  round2Date: '',
  round2Comment: '',
  round2Status: 'Pending',
  hrRoundDate: '',
  hrRoundFeedback: '',
  finalStatus: 'Pending',
  joiningDate: '',
  joiningVenue: '',
};

function TrackingModal({ isOpen, onClose, student }) {
  const [formData, setFormData] = useState(initialFormData);

  // When modal opens, load existing tracking data
  useEffect(() => {
    if (student.tracking && Object.keys(student.tracking).length > 0) {
      // Merge saved data with default to ensure all fields exist
      setFormData(prev => ({ ...initialFormData, ...student.tracking }));
    } else {
      // Reset the form if there's no data
      setFormData(initialFormData);
    }
  }, [student]); // This runs every time a new student is selected

  // Update state as the recruiter types
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newState = { ...prev, [name]: value };

      // --- CASCADING LOGIC ---
      // If rejected at assessment, reset all later rounds
      if (name === 'onlineAssessmentStatus' && value !== 'Shortlisted') {
        newState.round1Status = 'Pending';
        newState.round2Status = 'Pending';
        // Clear data for hidden fields
        newState.round1Date = '';
        newState.round1Comment = '';
        newState.round2Date = '';
        newState.round2Comment = '';
        newState.hrRoundDate = '';
        newState.hrRoundFeedback = '';
      }
      // If rejected at R1, reset R2 and HR
      if (name === 'round1Status' && value !== 'Shortlisted') {
        newState.round2Status = 'Pending';
        // Clear data for hidden fields
        newState.round2Date = '';
        newState.round2Comment = '';
        newState.hrRoundDate = '';
        newState.hrRoundFeedback = '';
      }
      // If rejected at R2, reset HR
      if (name === 'round2Status' && value !== 'Shortlisted') {
        // Clear data for hidden fields
        newState.hrRoundDate = '';
        newState.hrRoundFeedback = '';
      }
      return newState;
    });
  };

  // Handle the "Save" button click
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Send the entire formData object as the tracking data
      const { data: updatedApplication } = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/applications/${student.application_id}/tracking`, 
        formData // Send the whole new object
      );
      
      alert('Progress saved!');
      onClose(updatedApplication); // Pass the new data back on close
    } catch (error) {
      console.error('Error saving tracking data:', error);
      alert('Failed to save progress. (Is the backend connected?)');
    }
  };

  // Helper to render radio buttons
  const renderStatusButtons = (stageName, statusValue) => (
    <div className="status-radio-group">
      <label>
        <input 
          type="radio" 
          name={stageName} 
          value="Shortlisted" 
          checked={statusValue === 'Shortlisted'} 
          onChange={handleChange} 
        />
        Shortlisted
      </label>
      <label>
        <input 
          type="radio" 
          name={stageName} 
          value="Rejected" 
          checked={statusValue === 'Rejected'} 
          onChange={handleChange} 
        />
        Rejected
      </label>
      <label>
        <input 
          type="radio" 
          name={stageName} 
          value="Pending" 
          checked={statusValue === 'Pending' || !statusValue} 
          onChange={handleChange} 
        />
        Pending
      </label>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => onClose(null)} // Pass null if no save
      className="tracking-modal"
      overlayClassName="tracking-modal-overlay"
    >
      <h3>Tracking: {student.fullName}</h3>
      <p>{student.email}</p>
      
      <form onSubmit={handleSubmit} className="tracking-form">
        
        {/* --- Initial Screening --- */}
        <div className="form-group">
          <label>Initial Screening Comment</label>
          <textarea
            name="initialScreeningComment"
            value={formData.initialScreeningComment}
            onChange={handleChange}
          />
        </div>

        {/* --- Online Assessment --- */}
        <div className="form-stage">
          <h4>Online Assessment</h4>
          <div className="form-group-inline">
            <div className="form-group">
              <label>Score</label>
              <input
                type="number"
                name="onlineAssessmentScore"
                value={formData.onlineAssessmentScore}
                onChange={handleChange}
                placeholder="e.g., 85"
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              {renderStatusButtons('onlineAssessmentStatus', formData.onlineAssessmentStatus)}
            </div>
          </div>
        </div>

        {/* --- CONDITIONAL: Round 1 --- */}
        {formData.onlineAssessmentStatus === 'Shortlisted' && (
          <div className="form-stage">
            <h4>Round 1</h4>
            <div className="form-group">
              <label>Round 1 Date</label>
              <input
                type="date"
                name="round1Date"
                value={formData.round1Date}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Round 1 Comment</label>
              <textarea
                name="round1Comment"
                value={formData.round1Comment}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              {renderStatusButtons('round1Status', formData.round1Status)}
            </div>
          </div>
        )}

        {/* --- CONDITIONAL: Round 2 --- */}
        {formData.onlineAssessmentStatus === 'Shortlisted' && formData.round1Status === 'Shortlisted' && (
          <div className="form-stage">
            <h4>Round 2</h4>
            <div className="form-group">
              <label>Round 2 Date</label>
              <input
                type="date"
                name="round2Date"
                value={formData.round2Date}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Round 2 Comment</label>
              <textarea
                name="round2Comment"
                value={formData.round2Comment}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              {renderStatusButtons('round2Status', formData.round2Status)}
            </div>
          </div>
        )}

        {/* --- CONDITIONAL: HR Round --- */}
        {formData.onlineAssessmentStatus === 'Shortlisted' && 
         formData.round1Status === 'Shortlisted' && 
         formData.round2Status === 'Shortlisted' && (
          <div className="form-stage">
            <h4>HR Round</h4>
            <div className="form-group">
              <label>HR Round Date</label>
              <input
                type="date"
                name="hrRoundDate"
                value={formData.hrRoundDate}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>HR Round Feedback</label>
              <textarea
                name="hrRoundFeedback"
                value={formData.hrRoundFeedback}
                onChange={handleChange}
              />
            </div>
          </div>
        )}

        {/* --- Final Status & Joining --- */}
        <div className="form-stage">
          <h4>Final Decision</h4>
          <div className="form-group-inline">
            <div className="form-group">
              <label>Final Status</label>
              <select
                name="finalStatus"
                value={formData.finalStatus}
                onChange={handleChange}
              >
                <option value="Pending">Pending</option>
                <option value="Shortlisted">Shortlisted</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
            <div className="form-group">
              <label>Joining Date</label>
              <input
                type="date"
                name="joiningDate"
                value={formData.joiningDate}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Venue / Joining Details</label>
            <input
              type="text"
              name="joiningVenue"
              value={formData.joiningVenue}
              onChange={handleChange}
              placeholder="e.g., Bengaluru Office, 9 AM"
            />
          </div>
        </div>

        {/* --- Buttons --- */}
        <div className="form-buttons">
          <button type="submit" className="btn-save">Save Progress</button>
          <button type="button" onClick={() => onClose(null)} className="btn-cancel">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default TrackingModal;